import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useBrowsePresetApi, useGetSinglePresetApi } from '@aibees/shared/api';
import { Maybe } from '@aibees/shared/definition';
import {
  applyTableColumnModifierPresetOnConfig,
  OperationMode,
} from '@aibees/shared/features';
import {
  TableLayoutTemplate,
  TableLayoutTemplateItem,
  TemplateTabs,
} from '@aibees/shared/features/advance-table/components/table-layout-modifier-dialog';
import { useBoolean } from '@aibees/shared/hooks';
import { useAdvanceTableContext } from '../../../context';
import {
  TableLayoutTemplateContextData as ContextData,
  TableLayoutTemplateContextProps as ContextProps,
} from './types';

const context = createContext<ContextData>({} as ContextData);

export const { Provider } = context;

export const useTableLayoutTemplateContext = () => useContext(context);

export const TableLayoutTemplateProvider = ({ children }: ContextProps) => {
  const { config } = useAdvanceTableContext();
  const [id, setId] = useState<Maybe<string>>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [tab, setTab] = useState<TemplateTabs>(TemplateTabs.ALL);
  const [templateName, setTemplateName] = useState<Maybe<string>>();
  const [newlyCreatedId, setNewlyCreatedId] =
    useState<Maybe<string>>(undefined);
  const [appliedId, setAppliedId] = useState<Maybe<string>>(undefined);
  const [firstLoadCompleted, setFirstLoadCompleted] = useState(false);
  const isPrivate = useBoolean(false);
  const tableLayoutModifierDialogState = useBoolean();
  const [operationMode, setOperationMode] = useState<OperationMode>(
    OperationMode.CREATE
  );
  const [templateEditItem, setTemplateEditItem] =
    useState<Maybe<TableLayoutTemplateItem>>();
  const { data: selectedPreset } = useGetSinglePresetApi<string>({
    type: 'SELECTED_'.concat(config.columnModifierPresetType!),
  });

  const { data, refetch } = useBrowsePresetApi<TableLayoutTemplate>({
    type: config.columnModifierPresetType!,
    size: 25,
  });

  const selectedTemplate = useMemo(() => {
    return data?.content.find((x) => x.id === selectedPreset?.value);
  }, [data?.content, selectedPreset?.value]);

  const conf = useMemo(() => {
    return applyTableColumnModifierPresetOnConfig(
      config,
      selectedTemplate?.value
    );
  }, [config, selectedTemplate]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const templates = useMemo(() => {
    return data?.content ?? [];
  }, [data?.content]);

  useEffect(() => {
    if (!firstLoadCompleted && selectedPreset?.value) {
      if (!appliedId) {
        if (selectedPreset?.value) {
          setAppliedId(selectedPreset.value);
          setId(selectedPreset.value);
        } else {
          setId(undefined);
        }
      } else if (appliedId) {
        setId(appliedId);
      } else if (
        newlyCreatedId &&
        data?.content?.find((x) => x.id === newlyCreatedId)
      ) {
        setId(newlyCreatedId);
      }
      setFirstLoadCompleted(true);
    }
  }, [
    data?.content,
    selectedPreset,
    newlyCreatedId,
    appliedId,
    firstLoadCompleted,
    id,
  ]);

  const isApplyDisabled = useMemo(() => id === appliedId, [id, appliedId]);

  const contextValue = useMemo(
    () => ({
      id,
      setId,
      selectedTemplate,
      selectedPreset,
      templates,
      tab,
      setTab,
      handleSearch,
      searchValue,
      config: conf ?? config,
      templateName,
      setTemplateName,
      isPrivate,
      newlyCreatedId,
      setNewlyCreatedId,
      appliedId,
      setAppliedId,
      isApplyDisabled,
      refetchTemplates: refetch,
      tableLayoutModifierDialogState,
      operationMode,
      setOperationMode,
      templateEditItem,
      setTemplateEditItem,
    }),
    [
      id,
      selectedTemplate,
      selectedPreset,
      templates,
      tab,
      handleSearch,
      searchValue,
      conf,
      config,
      templateName,
      isPrivate.value,
      newlyCreatedId,
      appliedId,
      isApplyDisabled,
      refetch,
      tableLayoutModifierDialogState.value,
      setOperationMode,
      operationMode,
      templateEditItem,
      setTemplateEditItem,
    ]
  );

  return <Provider value={contextValue}>{children}</Provider>;
};
